import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, GripVertical, Ticket as TicketIcon } from "lucide-react";
import { TICKET_CATEGORIES, PriorityBangs } from "../components/TicketCard";

const SLOT_MIN = 30;
const SLOT_PX = 36;
const START_SLOT = 16; // 8 AM
const END_SLOT = 40;   // 8 PM
const SLOTS_VISIBLE = END_SLOT - START_SLOT;
const CATEGORIES = ["Meeting", "Client Work", "Sales", "Admin", "DeepThree Internal", "Other"];
const CATEGORY_COLORS = {
  Meeting: "#0066ff",
  "Client Work": "#7C3AED",
  Sales: "#10B981",
  Admin: "#F59E0B",
  "DeepThree Internal": "#EC4899",
  Other: "#71717A",
};

const todayISO = () => new Date().toISOString().slice(0, 10);

function slotIndexFromY(y) {
  const relative = Math.max(0, Math.min(SLOTS_VISIBLE - 1, Math.floor(y / SLOT_PX)));
  return START_SLOT + relative;
}
function slotToDate(slotIdx, dateISO) {
  const d = new Date(dateISO + "T00:00:00");
  d.setMinutes(d.getMinutes() + slotIdx * SLOT_MIN);
  return d;
}
function dateToSlot(iso) {
  const d = new Date(iso);
  return d.getHours() * 2 + Math.floor(d.getMinutes() / SLOT_MIN);
}
function formatHourLabel(slotIdx) {
  const h = Math.floor(slotIdx / 2);
  const m = slotIdx % 2 === 0 ? "00" : "30";
  const period = h >= 12 ? "PM" : "AM";
  const dispH = h % 12 === 0 ? 12 : h % 12;
  return `${dispH}:${m} ${period}`;
}

export default function TodoSchedule({ embedded = false }) {
  const navigate = useNavigate();
  const [todos, setTodos] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [pingedId, setPingedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const today = todayISO();
  const gridRef = useRef(null);

  const load = async () => {
    const [t, k] = await Promise.all([api.get("/todos"), api.get("/tickets?status=active")]);
    setTodos(t.data);
    setTickets(k.data);
  };
  useEffect(() => { load(); }, []);

  const todaysTodos = useMemo(
    () => todos.filter((t) => t.due_date && t.due_date.slice(0, 10) === today),
    [todos, today]
  );
  const unscheduled = todaysTodos.filter((t) => !t.scheduled_start);
  const scheduled = todaysTodos.filter((t) => t.scheduled_start);
  const scheduledTickets = useMemo(
    () => tickets.filter((tk) => tk.scheduled_start && tk.scheduled_start.slice(0, 10) === today),
    [tickets, today]
  );
  const unscheduledTickets = useMemo(
    () => tickets.filter((tk) => !tk.scheduled_start),
    [tickets]
  );

  const toggle = async (t) => {
    setPingedId(t.id);
    setTimeout(() => setPingedId(null), 320);
    setTodos((prev) => prev.map((x) => (x.id === t.id ? { ...x, completed: !x.completed } : x)));
    await api.put(`/todos/${t.id}`, { completed: !t.completed });
    load();
  };

  const onGridDrop = async (e) => {
    e.preventDefault();
    const todoId = e.dataTransfer.getData("text/todoId");
    const ticketId = e.dataTransfer.getData("text/ticketId");
    if (!todoId && !ticketId) return;
    const rect = gridRef.current.getBoundingClientRect();
    const slotIdx = slotIndexFromY(e.clientY - rect.top);
    const start = slotToDate(slotIdx, today);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    if (todoId) {
      await api.put(`/todos/${todoId}`, {
        scheduled_start: start.toISOString(),
        scheduled_end: end.toISOString(),
        due_date: today,
      });
    } else if (ticketId) {
      await api.put(`/tickets/${ticketId}`, {
        scheduled_start: start.toISOString(),
        scheduled_end: end.toISOString(),
      });
    }
    load();
  };

  const onUnscheduledDrop = async (e) => {
    e.preventDefault();
    const todoId = e.dataTransfer.getData("text/todoId");
    const ticketId = e.dataTransfer.getData("text/ticketId");
    if (todoId) await api.put(`/todos/${todoId}`, { scheduled_start: null, scheduled_end: null });
    if (ticketId) await api.put(`/tickets/${ticketId}`, { scheduled_start: null, scheduled_end: null });
    load();
  };

  return (
    <div data-testid="schedule-page">
      {!embedded && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Today</div>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">To-Do Schedule</h1>
            <p className="mt-2 text-sm text-zinc-600">Drag to-dos and work orders onto the timeline.</p>
          </div>
          <button data-testid="schedule-new-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] active:scale-[0.98]"><Plus size={16} /> Add To-Do</button>
        </div>
      )}

      <div className={embedded ? "grid grid-cols-12 gap-5" : "mt-6 grid grid-cols-12 gap-5"}>
        {/* Side panels */}
        <div
          className="col-span-12 md:col-span-3 flex flex-col gap-4"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onUnscheduledDrop}
        >
          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Unscheduled</span>
              <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-600">{unscheduled.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {unscheduled.map((t) => (
                <UnscheduledCard key={t.id} todo={t} pinged={pingedId === t.id} onToggle={() => toggle(t)} />
              ))}
              {unscheduled.length === 0 && (
                <div className="rounded-md border border-dashed border-zinc-200 p-4 text-center text-xs text-zinc-400">
                  All today's tasks are scheduled.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Active Work Orders</span>
              <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-600">{unscheduledTickets.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {unscheduledTickets.map((tk) => (
                <UnscheduledTicket key={tk.id} ticket={tk} onClick={() => navigate(`/tickets/${tk.id}`)} />
              ))}
              {unscheduledTickets.length === 0 && (
                <div className="rounded-md border border-dashed border-zinc-200 p-4 text-center text-xs text-zinc-400">
                  No active work orders to schedule.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Time grid */}
        <div className="col-span-12 md:col-span-9">
          <div className="rounded-xl border border-zinc-200 bg-white">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
              <span className="text-sm font-medium text-zinc-800">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
              <span className="text-xs text-zinc-500">8:00 AM → 8:00 PM</span>
            </div>
            <div
              ref={gridRef}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onGridDrop}
              className="relative"
              style={{ height: SLOTS_VISIBLE * SLOT_PX }}
              data-testid="schedule-grid"
            >
              {Array.from({ length: SLOTS_VISIBLE }).map((_, i) => {
                const absSlot = START_SLOT + i;
                return (
                  <div
                    key={i}
                    className={`absolute left-0 right-0 flex items-start ${absSlot % 2 === 0 ? "border-t border-zinc-200" : "border-t border-dashed border-zinc-100"}`}
                    style={{ top: i * SLOT_PX, height: SLOT_PX }}
                  >
                    {absSlot % 2 === 0 && (
                      <span className="ml-3 mt-1 text-[10px] font-medium uppercase tracking-widest text-zinc-400">
                        {formatHourLabel(absSlot)}
                      </span>
                    )}
                  </div>
                );
              })}
              {scheduled.map((t) => (
                <ScheduledBlock key={t.id} todo={t} pinged={pingedId === t.id} onToggle={() => toggle(t)} onReload={load} />
              ))}
              {scheduledTickets.map((tk) => (
                <ScheduledTicketBlock key={tk.id} ticket={tk} onOpen={() => navigate(`/tickets/${tk.id}`)} onReload={load} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {creating && (
        <QuickAddTodo onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function UnscheduledCard({ todo, pinged, onToggle }) {
  const color = CATEGORY_COLORS[todo.category] || "#71717A";
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/todoId", todo.id)}
      className="group flex items-center gap-2 rounded-lg border-l-2 bg-white px-3 py-2 shadow-sm transition-all hover:-translate-y-[1px] hover:shadow"
      style={{ borderLeftColor: color }}
      data-testid={`schedule-unscheduled-${todo.id}`}
    >
      <GripVertical size={12} className="text-zinc-300" />
      <button
        onClick={onToggle}
        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-md border transition-all ${todo.completed ? "border-[#0066ff] bg-[#0066ff]" : "border-zinc-300 bg-white"} ${pinged ? "dt-ping" : ""}`}
      >
        {todo.completed && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 12l5 5L20 7" /></svg>}
      </button>
      <span className={todo.completed ? "todo-complete-strike text-xs" : "text-xs text-zinc-800"}>{todo.title}</span>
    </div>
  );
}

function UnscheduledTicket({ ticket, onClick }) {
  const cat = TICKET_CATEGORIES[ticket.category] || TICKET_CATEGORIES.other;
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/ticketId", ticket.id)}
      onClick={onClick}
      className="group flex cursor-pointer items-center gap-2 rounded-lg border-l-2 bg-white px-3 py-2 shadow-sm transition-all hover:-translate-y-[1px] hover:shadow"
      style={{ borderLeftColor: cat.color }}
      data-testid={`schedule-unscheduled-ticket-${ticket.id}`}
    >
      <GripVertical size={12} className="text-zinc-300" />
      <TicketIcon size={11} className="text-zinc-400" />
      <span className="flex-1 truncate text-xs text-zinc-800">{ticket.title}</span>
      <PriorityBangs level={ticket.priority} />
    </div>
  );
}

function ScheduledBlock({ todo, pinged, onToggle, onReload }) {
  const blockRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [resizing, setResizing] = useState(false);

  const rawStart = dateToSlot(todo.scheduled_start);
  const rawEnd = todo.scheduled_end ? dateToSlot(todo.scheduled_end) : rawStart + 2;
  const outsideWindow = rawEnd <= START_SLOT || rawStart >= END_SLOT;
  const startSlot = Math.max(START_SLOT, rawStart);
  const endSlot = Math.min(END_SLOT, rawEnd);
  const slots = Math.max(1, endSlot - startSlot);
  const color = CATEGORY_COLORS[todo.category] || "#71717A";

  const onResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing(true);
    const startY = e.clientY;
    const startSlots = slots;
    const onMove = (ev) => {
      const delta = ev.clientY - startY;
      const newSlots = Math.max(1, Math.min(END_SLOT - startSlot, Math.round(startSlots + delta / SLOT_PX)));
      if (blockRef.current) {
        blockRef.current.style.height = `${newSlots * SLOT_PX - 2}px`;
        blockRef.current.dataset.newSlots = newSlots;
      }
    };
    const onUp = async () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setResizing(false);
      const newSlots = parseInt(blockRef.current?.dataset.newSlots || slots);
      if (newSlots !== slots) {
        const newEnd = new Date(new Date(todo.scheduled_start).getTime() + newSlots * SLOT_MIN * 60 * 1000);
        await api.put(`/todos/${todo.id}`, { scheduled_end: newEnd.toISOString() });
        onReload();
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (outsideWindow) return null;

  const startLabel = new Date(todo.scheduled_start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const endLabel = todo.scheduled_end ? new Date(todo.scheduled_end).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";

  return (
    <div
      ref={blockRef}
      draggable={!resizing}
      onDragStart={(e) => e.dataTransfer.setData("text/todoId", todo.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`schedule-block-${todo.id}`}
      className={`absolute left-20 right-3 overflow-hidden rounded-lg border-l-4 bg-white px-3 py-2 shadow-sm transition-shadow hover:shadow-md ${todo.completed ? "opacity-60" : ""}`}
      style={{
        top: (startSlot - START_SLOT) * SLOT_PX + 1,
        height: slots * SLOT_PX - 2,
        borderLeftColor: color,
        backgroundColor: todo.completed ? "#f4f4f5" : `${color}10`,
      }}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={onToggle}
          className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-md border transition-all ${todo.completed ? "border-[#0066ff] bg-[#0066ff]" : "border-zinc-300 bg-white"} ${pinged ? "dt-ping" : ""}`}
        >
          {todo.completed && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 12l5 5L20 7" /></svg>}
        </button>
        <div className="min-w-0 flex-1">
          <div className={todo.completed ? "todo-complete-strike text-sm font-medium" : "text-sm font-medium text-zinc-900"}>{todo.title}</div>
          <div className="mt-0.5 text-[10px] uppercase tracking-widest" style={{ color }}>
            {startLabel}{endLabel ? ` – ${endLabel}` : ""} · {todo.category}
          </div>
        </div>
      </div>
      <div
        onMouseDown={onResizeStart}
        className={`absolute inset-x-0 bottom-0 h-2 cursor-ns-resize ${hover ? "bg-zinc-300/40" : ""}`}
        title="Drag to resize"
      />
    </div>
  );
}

function ScheduledTicketBlock({ ticket, onOpen, onReload }) {
  const blockRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [resizing, setResizing] = useState(false);

  const rawStart = dateToSlot(ticket.scheduled_start);
  const rawEnd = ticket.scheduled_end ? dateToSlot(ticket.scheduled_end) : rawStart + 2;
  const outsideWindow = rawEnd <= START_SLOT || rawStart >= END_SLOT;
  const startSlot = Math.max(START_SLOT, rawStart);
  const endSlot = Math.min(END_SLOT, rawEnd);
  const slots = Math.max(1, endSlot - startSlot);
  const cat = TICKET_CATEGORIES[ticket.category] || TICKET_CATEGORIES.other;

  const onResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing(true);
    const startY = e.clientY;
    const startSlots = slots;
    const onMove = (ev) => {
      const delta = ev.clientY - startY;
      const newSlots = Math.max(1, Math.min(END_SLOT - startSlot, Math.round(startSlots + delta / SLOT_PX)));
      if (blockRef.current) {
        blockRef.current.style.height = `${newSlots * SLOT_PX - 2}px`;
        blockRef.current.dataset.newSlots = newSlots;
      }
    };
    const onUp = async () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setResizing(false);
      const newSlots = parseInt(blockRef.current?.dataset.newSlots || slots);
      if (newSlots !== slots) {
        const newEnd = new Date(new Date(ticket.scheduled_start).getTime() + newSlots * SLOT_MIN * 60 * 1000);
        await api.put(`/tickets/${ticket.id}`, { scheduled_end: newEnd.toISOString() });
        onReload();
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (outsideWindow) return null;

  const startLabel = new Date(ticket.scheduled_start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const endLabel = ticket.scheduled_end ? new Date(ticket.scheduled_end).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";

  return (
    <div
      ref={blockRef}
      draggable={!resizing}
      onDragStart={(e) => e.dataTransfer.setData("text/ticketId", ticket.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
      data-testid={`schedule-ticket-${ticket.id}`}
      className="absolute left-20 right-3 cursor-pointer overflow-hidden rounded-lg border-l-4 bg-white px-3 py-2 shadow-sm transition-shadow hover:shadow-md"
      style={{
        top: (startSlot - START_SLOT) * SLOT_PX + 1,
        height: slots * SLOT_PX - 2,
        borderLeftColor: cat.color,
        backgroundColor: `${cat.color}10`,
      }}
    >
      <div className="flex items-start gap-2">
        <TicketIcon size={14} style={{ color: cat.color }} className="mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-zinc-900">{ticket.title}</div>
          <div className="mt-0.5 text-[10px] uppercase tracking-widest" style={{ color: cat.color }}>
            {startLabel}{endLabel ? ` – ${endLabel}` : ""} · {cat.label}
          </div>
        </div>
        <PriorityBangs level={ticket.priority} />
      </div>
      <div
        onMouseDown={onResizeStart}
        className={`absolute inset-x-0 bottom-0 h-2 cursor-ns-resize ${hover ? "bg-zinc-300/40" : ""}`}
        title="Drag to resize"
      />
    </div>
  );
}

function QuickAddTodo({ onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState("Other");
  const [importance, setImportance] = useState(3);
  const save = async () => {
    if (!title.trim()) return toast.error("Title required");
    await api.post("/todos", { title, category: cat, importance, due_date: todayISO() });
    toast.success("Added");
    onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Quick Add</h2>
        <div className="mt-4 flex flex-col gap-3">
          <input data-testid="schedule-quick-title" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Task title" className="rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20" />
          <div className="grid grid-cols-2 gap-3">
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-md border border-zinc-200 px-3 py-2 text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={importance} onChange={(e) => setImportance(parseInt(e.target.value))} className="rounded-md border border-zinc-200 px-3 py-2 text-sm">
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Importance {n}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="schedule-quick-save" onClick={save} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc]">Add</button>
        </div>
      </div>
    </div>
  );
}
